import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Button, Paper, TextField, Typography } from '@mui/material';

import { isValidEmail } from '../../../../../utils/emailValidationUtils';

import { BackButton, PageTitle } from '../../../../../components/common';
import ButtonContainer from '../../../../../components/ButtonContainer';
import { INVALID_FIELD, VALID_FIELD } from '../../../../../shared';

import AddressForm from './AddressForm';
import { StyledVerticalContent } from './PersonalDetails.styled';

const validatePhoneNumber = (phone) => (phone ? phone.length >= 6 && phone.length <= 12 : true);

function PersonalDetailsForm(props) {
  const {
    backButtonLink,
    nextButtonLink,
    pageTitleId,
    fullname,
    address,
    houseNumber,
    postcode,
    city,
    email,
    telephone,
    onNameChange,
    onAddressChange,
    onHouseNumberChange,
    onPostcodeChange,
    onCityChange,
    onEmailChange,
    onTelephoneChange,
  } = props;

  const history = useHistory();

  const [startLiveValidation, setStartLiveValidation] = useState(false);
  const [postCodeStatus, setPostCodeStatus] = useState(VALID_FIELD);
  const [emailStatus, setEmailStatus] = useState(VALID_FIELD);
  const [phoneStatus, setPhoneStatus] = useState(VALID_FIELD);

  const validForm = !!fullname && !!address && !!houseNumber && !!postcode && !!city;

  const validateForm = useCallback(() => {
    let isValid = true;

    if (!postcode) {
      setPostCodeStatus(INVALID_FIELD);
      isValid = false;
    } else {
      setPostCodeStatus(VALID_FIELD);
    }

    if (email && !isValidEmail(email)) {
      setEmailStatus(INVALID_FIELD);
      isValid = false;
    } else {
      setEmailStatus(VALID_FIELD);
    }

    if (!validatePhoneNumber(telephone)) {
      setPhoneStatus(INVALID_FIELD);
      isValid = false;
    } else {
      setPhoneStatus(VALID_FIELD);
    }

    return isValid;
  }, [email, telephone, postcode]);

  useEffect(() => {
    if (!startLiveValidation) return;

    validateForm();
  }, [startLiveValidation, validateForm]);

  const handleButtonClick = (ev) => {
    if (ev) ev.preventDefault();

    if (!startLiveValidation) {
      setStartLiveValidation(true);
    }

    const isValid = validateForm();
    if (!isValid || !validForm) return;

    history.push(nextButtonLink);
  };

  const handlePhoneChange = (event) => {
    const { value } = event.target;
    if (/^([0-9]\d*)$/.test(value) || value === '') {
      onTelephoneChange(event);
    }
  };

  return (
    <>
      <BackButton to={backButtonLink}>
        <Typography variant="h3">
          <FormattedMessage id="common.button.back" />
        </Typography>
      </BackButton>

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id={pageTitleId} />
        </Typography>
      </PageTitle>

      <Paper>
        <StyledVerticalContent>
          <TextField
            fullWidth
            id="fullname"
            name="fullname"
            placeholder="Max Mustermann"
            label={
              <Typography variant="h3" component="span">
                <FormattedMessage id="new_claim.form.label.name" />
              </Typography>
            }
            value={fullname || ''}
            onChange={onNameChange}
          />

          <AddressForm
            address={address || ''}
            houseNumber={houseNumber || ''}
            postcode={postcode || ''}
            city={city || ''}
            onAddressChange={onAddressChange}
            onHouseNumberChange={onHouseNumberChange}
            onPostcodeChange={onPostcodeChange}
            onCityChange={onCityChange}
            postCodeStatus={postCodeStatus}
          />

          <TextField
            fullWidth
            type="email"
            id="email"
            name="email"
            placeholder="max@mustermann.com"
            label={
              <Typography variant="h3" component="span">
                <FormattedMessage id="new_claim.form.label.email.optional" />
              </Typography>
            }
            value={email || ''}
            onChange={onEmailChange}
            error={emailStatus === INVALID_FIELD}
            helperText={
              emailStatus === INVALID_FIELD && (
                <Typography variant="p" color="error" data-testid="error-email">
                  <FormattedMessage id="personal.details.form.email.error.message" />
                </Typography>
              )
            }
          />

          <TextField
            fullWidth
            type="tel"
            id="telephone"
            name="telephone"
            placeholder="0176 12345678"
            label={
              <Typography variant="h3" component="span">
                <FormattedMessage id="new_claim.form.label.phone.optional" />
              </Typography>
            }
            value={telephone || ''}
            onChange={handlePhoneChange}
            error={phoneStatus === INVALID_FIELD}
            helperText={
              phoneStatus === INVALID_FIELD && (
                <Typography variant="p" color="error" data-testid="error-phone">
                  <FormattedMessage id="personal.details.form.phone.error.message" />
                </Typography>
              )
            }
            slotProps={{
              htmlInput: {
                minLength: '6',
                maxLength: '12',
              },
            }}
          />
        </StyledVerticalContent>
      </Paper>

      <ButtonContainer>
        <Button onClick={handleButtonClick} disabled={!validForm} data-testid="button-next">
          <Typography variant="buttons">
            <FormattedMessage id="common.button.next" />
          </Typography>
        </Button>
      </ButtonContainer>
    </>
  );
}

export default PersonalDetailsForm;
